import { WORLD_CONFIG } from '../../../config/world.config';
import { DynamicEventType } from '../../../dynamic-events/DynamicEventType';
import { HazardKind, VehicleType, type VehicleHazard } from '../../../hazards/HazardDefinition';
import { ObstacleType } from '../../../obstacles/ObstacleType';
import { hazardSocket, laneX, mirroredHazard, mirroredVariant } from '../hazardBuilders';
import { COLLECTIBLE_FLOAT_HEIGHT, collectibleLanes, entryConnector, moduleSides, obstacleRow, standardLanes, straightExit } from '../segmentBuilders';
import { SegmentKind, type FixedObstaclePlacement, type ObstacleVariant, type SegmentDefinition } from '../SegmentDefinition';

/**
 * SEG_11 – forklift area 40 m (design/01_segments/SEG_11_Forklift). A loaded forklift waits at the
 * far end with its beacon on, sounds the horn and drives towards the runner (moving hazard, see
 * hazards/VehicleModel): it departs so that it meets the runner at z 15, whatever the run speed –
 * the closing speed is the runner's plus the forklift's. In two layouts it changes lanes on the
 * way (signal, S-curve). Pallet racks (slide under the 1.6 m beam) and pallet stacks (jump) stand
 * in the other lanes. Bottles before the meeting line in the forklift's lane are the risky route.
 *
 *   STRAIGHT_LEFT   forklift lane 0, forks low  · R5: L2 rack · R25: L2 pallets · R36: L0 rack
 *   CHANGE_RIGHT    forklift lane 2 → 1         · R5: L2 lane closure · R25: L0 rack · R36: L1 pallets
 *   STRAIGHT_RIGHT / CHANGE_LEFT = mirrored (forks high on the right)
 *
 * R5 L2 was a container until 2026-10-02: the forklift drives in lane 2 down to z 17.25 – inside the 15 m drop
 * zone behind its roof (level/terrain/roofRunUp.ts). The lane closure asks for the same lane change.
 *
 * Sheet "durch niedrige Gabeln rutschen, über hohe springen": a slide under the forks of an
 * oncoming forklift would end in its mast – the forklift is always dodged; racks are the slides.
 */
export const FORKLIFT_AREA = {
  length: 40,
  startZ: 30,
  meetZ: 15,
  stopZ: 4,
  laneChangeAtZ: 25,
  rows: { R5: 5, R25: 25, R36: 36 },
} as const;

const W = WORLD_CONFIG;
const GROUND_TYPES = [ObstacleType.Jump, ObstacleType.Slide, ObstacleType.LaneChange] as const;
const SOCKET_FORKLIFT = 'SOCKET_EVT_FORKLIFT';

const forklift = (id: string, lane: number, forks: VehicleHazard['forks'], toLane?: number): VehicleHazard => ({
  id,
  kind: HazardKind.Vehicle,
  vehicle: VehicleType.Forklift,
  socketId: SOCKET_FORKLIFT,
  lane,
  startZ: FORKLIFT_AREA.startZ,
  meetZ: FORKLIFT_AREA.meetZ,
  stopZ: FORKLIFT_AREA.stopZ,
  ...(toLane !== undefined ? { laneChange: { toLane, atZ: FORKLIFT_AREA.laneChangeAtZ } } : {}),
  forks,
  load: true,
});

const STRAIGHT_L0 = forklift('HAZ_FORKLIFT_L0', 0, 'LOW');
const STRAIGHT_L2: VehicleHazard = { ...mirroredHazard(W, 'HAZ_FORKLIFT_L2', STRAIGHT_L0), forks: 'HIGH' };
const CHANGE_L2_L1 = forklift('HAZ_FORKLIFT_L2_TO_L1', 2, 'LOW', 1);
const CHANGE_L0_L1: VehicleHazard = { ...mirroredHazard(W, 'HAZ_FORKLIFT_L0_TO_L1', CHANGE_L2_L1), forks: 'HIGH' };

const place = (row: keyof typeof FORKLIFT_AREA.rows, lane: number, obstacleId: string): FixedObstaclePlacement => ({
  spawnPointId: `SOCKET_OBS_${row}_L${lane}`,
  obstacleId,
});

const STRAIGHT_LEFT: ObstacleVariant = {
  id: 'STRAIGHT_LEFT',
  weight: 1,
  placements: [place('R5', 2, 'OBS_PALLET_RACK_A'), place('R25', 2, 'OBS_PALLET_A'), place('R36', 0, 'OBS_PALLET_RACK_A')],
  hazards: [STRAIGHT_L0.id],
};

const CHANGE_RIGHT: ObstacleVariant = {
  id: 'CHANGE_RIGHT',
  weight: 1,
  placements: [place('R5', 2, 'OBS_LANE_CLOSURE_A'), place('R25', 0, 'OBS_PALLET_RACK_A'), place('R36', 1, 'OBS_PALLET_A')],
  hazards: [CHANGE_L2_L1.id],
};

const SIDES = moduleSides(FORKLIFT_AREA.length);

export const SEG_FORKLIFT_40M: SegmentDefinition = {
  id: 'SEG_FORKLIFT_40M',
  kind: SegmentKind.Straight,
  displayName: 'Staplerbereich 40 m',
  length: FORKLIFT_AREA.length,
  width: W.trackWidth,
  visualAssetId: 'SEG_STRAIGHT_20M',
  extraVisuals: [{ assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 20 } }],

  entry: entryConnector(),
  exits: [straightExit(FORKLIFT_AREA.length)],
  lanes: standardLanes(W),

  obstacleSpawnPoints: Object.entries(FORKLIFT_AREA.rows).flatMap(([row, z]) => obstacleRow(W, row, z, GROUND_TYPES)),
  obstacleRowLayouts: { sparse: [], dense: [] },
  obstacleVariants: [
    STRAIGHT_LEFT,
    mirroredVariant(W, 'STRAIGHT_RIGHT', STRAIGHT_LEFT, [STRAIGHT_L2.id]),
    CHANGE_RIGHT,
    mirroredVariant(W, 'CHANGE_LEFT', CHANGE_RIGHT, [CHANGE_L0_L1.id]),
  ],
  hazards: [STRAIGHT_L0, STRAIGHT_L2, CHANGE_L2_L1, CHANGE_L0_L1],

  collectibleSpawnPoints: collectibleLanes(W, 1, FORKLIFT_AREA.length - 1, 2, COLLECTIBLE_FLOAT_HEIGHT),

  ...SIDES,
  dynamicEventSockets: [...SIDES.dynamicEventSockets, hazardSocket(SOCKET_FORKLIFT, laneX(W, 1), FORKLIFT_AREA.meetZ, DynamicEventType.ForkliftCrossing)],

  generation: {
    weight: 0.25,
    minDistance: 200,
    minSegmentsAfterJunction: 1,
    family: 'FORKLIFT',
    tags: ['forklift', 'hazard', 'vehicle', 'slide'],
  },
};
